import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import AgendaRecebimentos from '../components/AgendaRecebimentos';
import BaixasPorCompetencia from '../components/BaixasPorCompetencia';
import VisoesEstrategicas from '../components/VisoesEstrategicas';
import { resumoDashboard, visaoArquivos } from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Painel,
  StatusArquivoBadge,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { brl, formatDate, numero } from '../utils/formato';

export default function Dashboard() {
  const navigate = useNavigate();
  const [aba, setAba] = useState('acompanhamento');
  const [resumo, setResumo] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    Promise.all([resumoDashboard(), visaoArquivos()])
      .then(([r, a]) => {
        setResumo(r.data);
        setArquivos(a.data);
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar o painel.')))
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <LoadingSpinner />;

  const recentes = arquivos.slice(0, 8);

  return (
    <>
      <PageHeader
        titulo="Visão geral"
        subtitulo="Quanto há para baixar, o que entrou e o que precisa de atenção."
        acoes={
          <Link to="/arquivos/novo" className="ff-btn ff-btn--primario">
            <i className="bi bi-upload" aria-hidden="true" />
            Lançar recebimento
          </Link>
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${aba === 'acompanhamento' ? 'active' : ''}`}
            onClick={() => setAba('acompanhamento')}
          >
            Acompanhamento
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${aba === 'gestao' ? 'active' : ''}`}
            onClick={() => setAba('gestao')}
          >
            Análise de gestão
          </button>
        </li>
      </ul>

      {aba === 'gestao' && <VisoesEstrategicas />}

      {aba === 'acompanhamento' && (
        <>

      <div className="ff-grid-tiles mb-4">
        <Indicador
          natureza="financeiro"
          rotulo="Total a baixar"
          valor={brl(resumo?.total_a_baixar)}
          nota={`${numero(resumo?.qtd_protocolos)} protocolo(s)`}
        />
        <Indicador
          natureza="entrada"
          rotulo="Arquivos recebidos"
          valor={numero(resumo?.qtd_arquivos)}
          nota="demonstrativos processados"
        />
        <Indicador
          natureza={resumo?.qtd_pendencias ? 'atencao' : 'neutro'}
          rotulo="Pendências"
          valor={numero(resumo?.qtd_pendencias)}
          nota="guias sem protocolo"
        />
        <Indicador
          natureza={resumo?.qtd_arquivos_reprovados ? 'atencao' : 'neutro'}
          rotulo="Arquivos reprovados"
          valor={numero(resumo?.qtd_arquivos_reprovados)}
          nota="não fecharam as conferências"
        />
      </div>

      <div className="mb-3">
        <BaixasPorCompetencia />
      </div>

      {/* A agenda vem depois das baixas de propósito: primeiro o que há para
          fazer, depois o que está faltando chegar. */}
      <div className="mb-3">
        <AgendaRecebimentos />
      </div>

      <Painel
        titulo="Últimos arquivos"
        subtitulo="Os demonstrativos processados mais recentemente — clique para ver o que cada um manda baixar"
        acoes={
          arquivos.length > 0 && (
            <Link to="/arquivos" className="ff-btn ff-btn--secundario ff-btn--pequeno">
              Ver todos
            </Link>
          )
        }
      >
        {recentes.length === 0 ? (
          <Vazio
            icone="bi-inbox"
            titulo="Nenhum arquivo recebido ainda"
            descricao="Envie o demonstrativo de pagamento de uma operadora para começar a conciliação."
            acao={
              <Link to="/arquivos/novo" className="ff-btn ff-btn--primario">
                Lançar recebimento
              </Link>
            }
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>Operadora</Th>
                <Th>Arquivo</Th>
                <Th>
                  Competência
                  <Ajuda titulo="Competência" origem="Informada no upload · cabeçalho do demonstrativo">
                    Mês em que a operadora efetuou o pagamento. Não é o mês do atendimento —
                    um único pagamento costuma cobrir atendimentos de vários meses.
                  </Ajuda>
                </Th>
                <Th alinhar="right">Guias</Th>
                <Th alinhar="right">
                  Valor
                  <Ajuda
                    titulo="Valor bruto pago"
                    origem="Arquivo da operadora · soma de Valor (R$)"
                  >
                    Total que a operadora pagou neste arquivo, antes dos impostos retidos.
                  </Ajuda>
                </Th>
                <Th>Status</Th>
                <Th alinhar="right">Recebido em</Th>
              </tr>
            </thead>
            <tbody>
              {recentes.map((a) => (
                <tr
                  key={a.id_arquivo}
                  className="ff-linha-clicavel"
                  title="Ver quanto este arquivo manda baixar, protocolo a protocolo"
                  // Clicar num arquivo aqui significa "o que tenho que baixar
                  // por causa dele" — nao "como foi a extracao". O detalhe do
                  // arquivo continua a um clique, na tela de Arquivos.
                  onClick={() => navigate(`/protocolos?arquivo=${a.id_arquivo}`)}
                >
                  <td className="col-principal">{a.operadora}</td>
                  <td className="col-codigo">{a.nome_original}</td>
                  <td className="col-meta">{a.competencia || '—'}</td>
                  <td className="col-qtd">{numero(a.qtd_guias)}</td>
                  <td className="col-valor">{brl(a.total_extraido)}</td>
                  <td>
                    <StatusArquivoBadge status={a.status} />
                  </td>
                  <td className="col-data">{formatDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </Painel>
        </>
      )}
    </>
  );
}
